import React, { useMemo, useState } from "react";
import {
  Box,
  IconButton,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import useStore from "../../helpers/useStore";

import { PatientsContext } from "../../stores/Patients/patients.provider";
import { observer } from "mobx-react";
import { useNavigate } from "react-router-dom";
import { CalendarCommands } from "../../stores/Calendar/calendar.commands";
import { CalendarContext } from "../../stores/Calendar/calendar.provider";
import { EditCalendar, ErrorOutline, ExpandLess, ExpandMore } from "@mui/icons-material";
import { mobiliTheme } from "../../themes/mobiliTheme";
import { VisitTypeBadge } from "../../atoms/Chip";
import { useUnscheduledAppointments } from "../../stores/Appointment/appointment.queries";
import { useMultiplePatientNotes } from "../../stores/Notes/notes.queries";
import { useLocationId } from "../../stores/User/User.hooks";
import { CancelledAndUnscheduledAppointmentsResponseDto } from "../../api-client";
import { PatientCommands } from "../../stores/Patients/patient.commands";
import { PATIENT_SUBTABS } from "../../stores/Patients/patientsStore";
import { groupAppointmentsBySignature } from "../../helpers/appointmentGrouping";
import { slotTherapyLabel, slotTherapyList, slotsInChronologicalOrder } from "../../helpers/slotTherapy";
import { GapDays } from "../../molecules/GapDays";

/**
 * The Heilmittel cell: therapy short codes as plain text, one line, clipped
 * to the cell. A table's rows and columns already delimit the values, so the
 * former per-slot pill chips only added drawn boxes (design-system: no pills
 * in table cells). The Rx numbers live in the tooltip, as before.
 */
const TherapyCellText = ({ timeSlots }: { timeSlots: any[] }) => (
  <Tooltip
    title={slotsInChronologicalOrder(timeSlots)
      .map(
        (timeSlot: any) =>
          `${slotTherapyLabel(timeSlot)} ${timeSlot.therapyRx?.rx?.rxNumber || "Verkauf"}`
      )
      .join(", ")}
  >
    <span className="block min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-dense font-semibold text-ink-text">
      {slotTherapyList(timeSlots, " · ")}
    </span>
  </Tooltip>
);

const UnscheduledAppointments = observer(() => {
  const navigate = useNavigate();
  const calendarStore = useStore(CalendarContext);
  const { navigateToCalendar } = CalendarCommands(calendarStore);

  const PatientStore = useStore(PatientsContext);
  const { navigateToPatient } = PatientCommands(PatientStore);

  const locationId = useLocationId();
  const rank = false;
  const { data } = useUnscheduledAppointments(locationId, rank);

  const unscheduledAppointments = (data || {
    unScheduledAppointments: [],
  }) as CancelledAndUnscheduledAppointmentsResponseDto;

  // Derive unique patient IDs for notes fetching
  const uniquePatientIds = useMemo(() => {
    if (!unscheduledAppointments?.unScheduledAppointments) return [];
    return Array.from(
      new Set(unscheduledAppointments.unScheduledAppointments.map((a: any) => String(a.patientId)))
    );
  }, [unscheduledAppointments?.unScheduledAppointments]);

  // Fetch notes for all patients using React Query (cached & invalidated properly)
  const { notesByPatientId } = useMultiplePatientNotes(uniquePatientIds);

  // Popover with the full latest note, keyed by patient
  const [noteAnchors, setNoteAnchors] = useState<{ [key: string]: HTMLElement | null }>({});
  const openNote = (id: string) => Boolean(noteAnchors[id]);

  const handlePatientData = (patient: any) => {
    navigateToPatient(navigate, patient.id, null, PATIENT_SUBTABS.APPOINTMENTS);
  };

  const handlePlanning = (patientId: string) => {
    navigateToCalendar(navigate, {
      patientId: Number(patientId),
    });
  };

  // Track which therapy-groups are expanded (key = patientId::signature)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const renderAppointmentTable = () => {
    const uniquePatients: any = {};

    return (
      <TableContainer
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          backgroundColor: mobiliTheme.cardBackground,
        }}
      >
        <Table size="small" stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableHead
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1,
              background: mobiliTheme.cardBackground,
            }}
          >
            {/* Fixed layout: every sized column is pinned to what its content
                actually needs, so the one unsized column — Hinweise — takes
                all remaining width. */}
            <TableRow>
              <TableCell sx={{ py: 0.5, width: 150 }}>Patient</TableCell>
              <TableCell sx={{ py: 0.5, width: 110 }}>Heilmittel</TableCell>
              <TableCell sx={{ py: 0.5, width: 56 }}>Ort</TableCell>
              <TableCell sx={{ py: 0.5 }}>Hinweise</TableCell>
              <TableCell sx={{ py: 0.5, width: 64 }}>Lücke</TableCell>
              <TableCell sx={{ py: 0.5, width: 56 }}>Planen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {unscheduledAppointments.unScheduledAppointments?.map((unscheduledAppointment: any) => {
              const patientId = unscheduledAppointment.patientId;
              const patientName = `${unscheduledAppointment.patient?.firstName ?? ""} ${unscheduledAppointment.patient?.lastName ?? ""}`.trim();

              // Skip if this patient has already been processed
              if (uniquePatients[patientId]) return null;
              uniquePatients[patientId] = true;

              // Group appointments by therapy signature
              const groups = groupAppointmentsBySignature(
                unscheduledAppointment.appointments || []
              );

              const patientNotesArr = notesByPatientId[String(patientId)] || [];
              const notesCount = patientNotesArr.length;
              const latestNote = patientNotesArr
                .slice()
                .sort(
                  (a: any, b: any) =>
                    new Date(b.updatedAt || b.createdAt || 0).getTime() -
                    new Date(a.updatedAt || a.createdAt || 0).getTime()
                )[0];
              const latestText = latestNote?.text || "";

              return (
                <React.Fragment key={unscheduledAppointment.patientId}>
                  {groups.map((group, gIdx) => {
                    const groupKey = `${patientId}::${group.signature}`;
                    const isExpanded = expandedGroups.has(groupKey);
                    const firstAppointment = group.representative;

                    return (
                      <React.Fragment key={groupKey}>
                        {/* Main summary row per group */}
                        <TableRow
                          sx={{
                            "&:hover": { backgroundColor: "rgb(var(--ink) / 0.02)" },
                            borderLeft: group.count > 1 ? "3px solid" : "none",
                            borderLeftColor: group.count > 1 ? "primary.main" : "transparent",
                          }}
                        >
                          {/* Patient Name Cell — only on first group row. The
                              appointment count that floated here as a badge is
                              gone: each group row already says ×N. */}
                          <TableCell sx={{ py: 0.5 }}>
                            {gIdx === 0 ? (
                              <Typography
                                onClick={() => handlePatientData(unscheduledAppointment.patient)}
                                sx={{
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  maxWidth: "100%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  cursor: "pointer",
                                  "&:hover": { textDecoration: "underline" },
                                }}
                              >
                                {patientName}
                              </Typography>
                            ) : null}
                          </TableCell>

                          {/* Therapy Cell with stack count */}
                          <TableCell sx={{ py: 0.5 }}>
                            <div className="flex min-w-0 items-center gap-1">
                              <div className="min-w-0 flex-1">
                                <TherapyCellText timeSlots={firstAppointment?.timeSlots} />
                              </div>
                              {group.count > 1 && (
                                <Tooltip
                                  title={
                                    isExpanded
                                      ? "Einklappen"
                                      : `${group.count} identische Termine – klicken zum Aufklappen`
                                  }
                                >
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => toggleGroup(groupKey)}
                                    onKeyDown={(event) => {
                                      if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        toggleGroup(groupKey);
                                      }
                                    }}
                                    className="inline-flex shrink-0 cursor-pointer items-center text-dense font-semibold tabular-nums text-ink-muted"
                                  >
                                    ×{group.count}
                                    {isExpanded ? (
                                      <ExpandLess sx={{ fontSize: 14 }} />
                                    ) : (
                                      <ExpandMore sx={{ fontSize: 14 }} />
                                    )}
                                  </span>
                                </Tooltip>
                              )}
                            </div>
                          </TableCell>

                          {/* Visit Cell */}
                          <TableCell sx={{ py: 0.5, width: 56, overflow: "hidden" }}>
                            {firstAppointment?.address?.type ? (
                              <VisitTypeBadge
                                visitType={firstAppointment?.address?.type}
                                visitFee={
                                  firstAppointment?.timeSlots?.find(
                                    (slot: any) => slot?.visitFee?.abbreviation
                                  )?.visitFee?.abbreviation || null
                                }
                              />
                            ) : (
                              <ErrorOutline sx={{ color: mobiliTheme.palette.info.light }} />
                            )}
                          </TableCell>

                          {/* Hinweise Cell — only on first group. Plain text
                              filling the cell; the note count rides inline
                              instead of as a floating badge. */}
                          <TableCell sx={{ py: 0.5, overflow: "hidden" }}>
                            {gIdx === 0 ? (
                              latestText ? (
                                <>
                                  <Tooltip title={latestText}>
                                    <div
                                      className="flex min-w-0 cursor-pointer items-baseline gap-1"
                                      aria-describedby={
                                        openNote(String(patientId))
                                          ? `note-popover-${patientId}`
                                          : undefined
                                      }
                                      onClick={(e) =>
                                        setNoteAnchors((prev) => ({
                                          ...prev,
                                          [String(patientId)]: e.currentTarget as HTMLElement,
                                        }))
                                      }
                                    >
                                      <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-dense text-ink-text">
                                        {latestText}
                                      </span>
                                      {notesCount > 1 && (
                                        <span className="shrink-0 text-mini font-semibold tabular-nums text-ink-muted">
                                          +{notesCount - 1}
                                        </span>
                                      )}
                                    </div>
                                  </Tooltip>
                                  <Popover
                                    id={`note-popover-${patientId}`}
                                    open={openNote(String(patientId))}
                                    anchorEl={noteAnchors[String(patientId)]}
                                    onClose={() => setNoteAnchors({})}
                                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                                    PaperProps={{
                                      sx: {
                                        width: { xs: "80vw", sm: "60vw", md: 420 },
                                        maxWidth: 480,
                                        maxHeight: "50vh",
                                        overflowY: "auto",
                                        p: 1,
                                      },
                                    }}
                                  >
                                    <Typography
                                      sx={{ fontSize: 13, whiteSpace: "pre-wrap" }}
                                    >
                                      {latestText}
                                    </Typography>
                                  </Popover>
                                </>
                              ) : (
                                <span className="text-dense text-ink-muted">–</span>
                              )
                            ) : null}
                          </TableCell>

                          {/* Gap Cell */}
                          <TableCell sx={{ py: 0.5, width: 64 }}>
                            <GapDays
                              gapInDays={firstAppointment?.gapInDays}
                              firstMatchesCancelled={
                                firstAppointment?.firstLastCancelled?.firstMatchesCancelled
                              }
                              lastMatchesCancelled={
                                firstAppointment?.firstLastCancelled?.lastMatchesCancelled
                              }
                            />
                          </TableCell>

                          {/* Actions Cell */}
                          <TableCell sx={{ py: 0, width: 56 }}>
                            <Tooltip title="Planen">
                              <IconButton
                                size="small"
                                onClick={() => handlePlanning(patientId)}
                              >
                                <EditCalendar sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>

                        {/* Expanded individual appointments within this group */}
                        {group.count > 1 &&
                          isExpanded &&
                          group.appointments.slice(1).map((appt: any, aIdx: number) => (
                            <TableRow
                              key={`${groupKey}-${aIdx}`}
                              sx={{
                                backgroundColor: "rgb(var(--ink) / 0.03)",
                                borderLeft: "3px solid",
                                borderLeftColor: "primary.light",
                                "& td": { py: 0.25 },
                              }}
                            >
                              <TableCell />
                              <TableCell>
                                <TherapyCellText timeSlots={appt?.timeSlots} />
                              </TableCell>
                              <TableCell sx={{ width: 64 }}>
                                {appt?.address?.type ? (
                                  <VisitTypeBadge
                                    visitType={appt?.address?.type}
                                    visitFee={
                                      appt?.timeSlots?.find(
                                        (slot: any) => slot?.visitFee?.abbreviation
                                      )?.visitFee?.abbreviation || null
                                    }
                                  />
                                ) : (
                                  <ErrorOutline
                                    sx={{ color: mobiliTheme.palette.info.light }}
                                  />
                                )}
                              </TableCell>
                              <TableCell />
                              <TableCell sx={{ width: 64 }}>
                                <GapDays
                                  gapInDays={appt?.gapInDays}
                                  firstMatchesCancelled={
                                    appt?.firstLastCancelled?.firstMatchesCancelled
                                  }
                                  lastMatchesCancelled={
                                    appt?.firstLastCancelled?.lastMatchesCancelled
                                  }
                                />
                              </TableCell>
                              <TableCell sx={{ py: 0, width: 56 }}>
                                <Tooltip title="Planen">
                                  <IconButton
                                    size="small"
                                    onClick={() => handlePlanning(patientId)}
                                  >
                                    <EditCalendar sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {unscheduledAppointments && renderAppointmentTable()}
    </Box>
  );
});

export default UnscheduledAppointments;
